import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { Cushion, Rectangle, Typography } from '@pitchero/react-ui';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import clubUrl from '../../lib/routing/club-url';
import { fetchSponsoredArticlePageData } from '../../store/sponsored-content/actions';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import {
  getArticle,
  hasAttemptedArticleLoad,
  hasLoadedArticle,
  isLoadingArticle,
  getSponsoredPageLoadingError,
} from '../../store/sponsored-content/selectors';
import { getMessage } from '../../store/activity-stream/selectors';
import NewsArticle from '../../components/news/article';
import SeoHead from '../../components/seo';
import { Trans } from '../../lib/i18n';

class SponsoredContentPage extends React.Component {
  componentDidMount() {
    const { club, hasLoaded, loadingError, loadArticle, contentId } = this.props;

    if (!hasLoaded && !loadingError) {
      loadArticle(club.id, contentId);
    }
  }

  componentDidUpdate(prevProps) {
    const { contentId } = this.props;
    const { contentId: previousArticleId } = prevProps;
    if (contentId !== previousArticleId) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const { isLoading, previouslyLoaded } = this.props;
    if (!isLoading && !previouslyLoaded) {
      const { club, loadArticle, contentId } = this.props;
      loadArticle(club.id, contentId);
    }
  };

  title = () => {
    const { article, message } = this.props;

    return get(article, 'title', get(message, 'title', 'Loading'));
  };

  image = () => {
    const { article, message } = this.props;

    return get(article, 'image', get(message, 'image'));
  };

  render() {
    const {
      loadingError,
      club,
      article: { bodyHtml: body, tagline, title, published, author, vimeoId } = {},
      isLoading,
      contentId,
    } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const shareUrl = clubUrl(club, 'news.sponsored', { _title: title, id: contentId });

    return (
      <>
        <SeoHead
          club={club}
          title={this.title()}
          description={tagline}
          routeParams={{ _title: title, id: contentId }}
          route="news.sponsored"
        />
        <NewsArticle
          club={club}
          author={{ name: author }}
          body={body}
          tagline={tagline}
          title={this.title()}
          image={this.image()}
          isLoading={isLoading}
          shareProps={{
            url: shareUrl,
            title,
            imageUrl: this.image(),
          }}
          renderSubheader={() => (
            <Cushion top="large" bottom="small" component="div">
              <Rectangle fill="primary" radius="rounded">
                <Cushion all="xsmall">
                  <Typography preset="name" color="primaryContrast">
                    <Trans i18nKey="sponsored_text">Sponsored</Trans>
                  </Typography>
                </Cushion>
              </Rectangle>
            </Cushion>
          )}
          articleId={contentId}
          published={published}
          sponsored
          vimeoId={vimeoId}
        />
      </>
    );
  }
}

SponsoredContentPage.getInitialProps = async ({ club, query, isServer, reduxStore, res, req }) => {
  const contentId = parseInt(query.id, 10);
  const initialProps = {
    contentId,
    activeSection: 'news',
    namespacesRequired: ['common'],
    pageType: 'news.sponsored',
    referringPlacement: query.placement,
  };
  if (!isServer) {
    return initialProps;
  }

  try {
    const { error } = await reduxStore.dispatch(
      fetchSponsoredArticlePageData(club.id, contentId, req),
    );
    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }
    return initialProps;
  } catch (err) {
    return initialProps;
  }
};

SponsoredContentPage.defaultProps = {
  article: null,
  message: null,
  loadingError: null,
};

SponsoredContentPage.propTypes = {
  article: PropTypes.shape({
    body_html: PropTypes.string,
    image_url: PropTypes.string,
    title: PropTypes.string,
    tagline: PropTypes.string,
    published: PropTypes.string,
    vimeoId: PropTypes.string,
  }),
  contentId: PropTypes.number.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadArticle: PropTypes.func.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  message: PropTypes.shape({
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  sponsored: PropTypes.bool.isRequired,
};

const dispatchToProps = {
  loadArticle: fetchSponsoredArticlePageData,
};

const mapStateToProps = (state, ownProps) => ({
  article: getArticle(state, ownProps.contentId),
  hasLoaded: hasAttemptedArticleLoad(state, ownProps.contentId),
  isLoading: isLoadingArticle(state, ownProps.contentId),
  message: getMessage(state, `news-${ownProps.contentId}`),
  loadingError: getSponsoredPageLoadingError(state, ownProps.contentId),
  previouslyLoaded: hasLoadedArticle(state, ownProps.contentId),
  sponsored: true,
});

export default connect(mapStateToProps, dispatchToProps)(SponsoredContentPage);
